import React, { useEffect, useState, useMemo, useRef, } from 'react'
import {
    Constants,
    MeetingProvider,
    MeetingConsumer,
    useMeeting,
    useParticipant,
    useWhiteboard
} from "@videosdk.live/react-sdk";
import Chat from './Chat';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Dropdown } from "react-bootstrap";
import recordingBlink from "./animations/recording-blink.json";
import useIsRecording from "./hooks/useIsRecording"
import { OutlinedButton } from './OutlinedButton';
import RecordingIcon from './RecordingIcon';
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from 'react-redux';
import { postStartRecordingData } from '../../Redux/action/MasterAction';
import MeetingTimer from './MeetingTimer';
import MeetingExtend from './MeetingExtend';
import toast from 'react-hot-toast';
import { usePubSub } from "@videosdk.live/react-sdk";


const styles = {
    controls: {
        position: "fixed",
        bottom: "0",
        left: "0",
        width: "100%",
        zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        padding: "10px"
    }
};

const ConfirmationModal = ({ show, onConfirm, onCancel, message }) => {
    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Body className="text-center p-4">
                <p className="mb-3">{message}</p>
                <div className="d-flex justify-content-center gap-2">
                    <Button variant="danger" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={onConfirm}>
                        Continue
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ADD THIS COMPONENT
const ParticipantView = React.memo(({ participantId, small, isHost, activeDrawerId, localParticipant, tutorId }) => {
    const { webcamStream, screenShareStream, displayName, micStream } = useParticipant(participantId);
    const videoRef = useRef(null);


    const [videoReady, setVideoReady] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        if (!micStream?.track || micStream.track.readyState !== "live") {
            setIsSpeaking(false);

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }

            if (analyserRef.current) {
                analyserRef.current.disconnect();
                analyserRef.current = null;
            }

            if (
                audioContextRef.current &&
                audioContextRef.current.state !== "closed"
            ) {
                audioContextRef.current.close().catch(() => { });
                audioContextRef.current = null;
            }

            return;
        }

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        const stream = new MediaStream([micStream.track]);
        const source = audioContext.createMediaStreamSource(stream);

        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const checkSpeaking = () => {
            if (micStream.track.readyState !== "live") {
                setIsSpeaking(false);

                if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                    animationRef.current = null;
                }

                return;
            }

            analyser.getByteFrequencyData(dataArray);

            const volume =
                dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

            setIsSpeaking(volume > 12);

            animationRef.current = requestAnimationFrame(checkSpeaking);
        };

        checkSpeaking();

        return () => {
            setIsSpeaking(false);

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }

            source.disconnect();
            analyser.disconnect();

            if (audioContext.state !== "closed") {
                audioContext.close().catch(() => { });
            }
        };
    }, [micStream?.track]);

    const lastTrackIdRef = useRef(null);

    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return;

        const activeStream = screenShareStream || webcamStream;

        if (!activeStream?.track) {
            console.log("[VIDEO] No active track, skipping");
            return;
        }

        if (lastTrackIdRef.current === activeStream.track.id) {
            console.log("[VIDEO] Same track detected, skip rebind");
            return;
        }

        lastTrackIdRef.current = activeStream.track.id;

        let mediaStream = videoEl.srcObject;

        if (!(mediaStream instanceof MediaStream)) {
            mediaStream = new MediaStream();
        } else {
            mediaStream.getTracks().forEach(track => {
                mediaStream.removeTrack(track);
            });
        }

        mediaStream.addTrack(activeStream.track);
        videoEl.srcObject = mediaStream;


        const playVideo = async () => {
            try {
                if (videoEl.paused) {
                    // await videoEl.play();
                    if (videoEl.isConnected) {
                        await videoEl.play();
                    }
                    console.log("[VIDEO] Playback resumed");
                }
            } catch (err) {
                console.log("[VIDEO ERROR]", err);
            }
        };

        playVideo();

    }, [webcamStream?.track?.id, screenShareStream?.track?.id]);

    const getInitials = (name) => {
        if (!name) return "U";

        const parts = name.trim().split(" ");

        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }

        return (
            (parts[0][0] || "") +
            (parts[parts.length - 1][0] || "")
        ).toUpperCase();
    };


    return (
        <div style={{
            position: "relative",
            width: "100%",
            height: "100%",
            border: isHost ? "3px solid gold" : "1px solid #333",
            borderRadius: "8px",
            overflow: "hidden"
        }}>
            {isHost && (
                <div style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "gold",
                    color: "#000",
                    padding: "2px 6px",
                    fontSize: "10px",
                    borderRadius: "5px",
                    zIndex: 2,
                    fontWeight: "bold"
                }}>
                    👑 Host
                </div>
            )}

            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                // style={{
                //     width: "100%",
                //     height: "100%",
                //     objectFit: "cover",
                //     background: "#000"
                // }}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    background: "#000",
                    imageRendering: "auto",
                    filter: "contrast(1.08) brightness(1.05) saturate(1.1)",
                    transform: "translateZ(0)",
                    backfaceVisibility: "hidden"
                }}
            />

            {/* {!webcamStream && !screenShareStream && (
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#222", background: "#000",
                    }} />

                )} */}

            {/* No camera avatar placeholder */}
            {!webcamStream && !screenShareStream && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, #1f2937, #111827)",
                        flexDirection: "column"
                    }}
                >
                    {/* Avatar + speaking ring */}
                    <div
                        style={{
                            position: "relative",
                            width: small ? "75px" : "130px",
                            height: small ? "75px" : "130px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        {/* Only show animation when speaking */}
                        {isSpeaking && (
                            <>
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "115%",
                                        height: "115%",
                                        border: "3px solid rgba(0,255,200,0.55)",
                                        borderRadius: "50%",
                                        animation: "meetingPulse 1.5s infinite ease-out"
                                    }}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "130%",
                                        height: "130%",
                                        border: "3px solid rgba(0,255,200,0.35)",
                                        borderRadius: "50%",
                                        animation: "meetingPulse 2s infinite ease-out"
                                    }}
                                />
                            </>
                        )}

                        {/* Main avatar */}
                        <div
                            style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontSize: small ? "28px" : "48px",
                                fontWeight: "700",
                                textTransform: "uppercase",
                                boxShadow: isSpeaking
                                    ? "0 0 22px rgba(0,255,200,0.5)"
                                    : "0 4px 12px rgba(0,0,0,0.35)",
                                transition: "all 0.25s ease"
                            }}
                        >
                            {getInitials(displayName)}
                        </div>
                    </div>

                    {/* Name below avatar */}
                    <div
                        style={{
                            marginTop: "10px",
                            color: "#fff",
                            fontSize: small ? "13px" : "18px",
                            fontWeight: "500",
                            textAlign: "center",
                            padding: "0 8px"
                        }}
                    >
                        {displayName || "User"}
                    </div>

                    {/* Animation */}
                    <style>
                        {`
            @keyframes meetingPulse {
                0% {
                    transform: scale(0.95);
                    opacity: 0.9;
                }
                70% {
                    transform: scale(1.28);
                    opacity: 0;
                }
                100% {
                    transform: scale(1.4);
                    opacity: 0;
                }
            }
            `}
                    </style>
                </div>
            )}

            {/* <div style={{
                    position: "absolute",
                    bottom: "5px",
                    left: "5px",
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    padding: "3px 8px",
                    fontSize: "12px",
                    borderRadius: "6px"
                }}>
                    {displayName || "User"}
                </div> */}
        </div>
    );
});



const Controls = ({ meetingId, user_name, user_type, participantDetails, onMeetingLeave, endTime, location }) => {
    const [meetingEndTime, setMeetingEndTime] = useState(endTime);
    const currentTime = new Date();
    const meetingEndTimes = new Date(meetingEndTime);
    const [presenterId, setPresenterId] = useState(null);
    const timeDifference = meetingEndTimes - currentTime;
    const { screenShareOn } = useParticipant(presenterId);
    const dispatch = useDispatch()
    // console.log('participantDetails',participantDetails)
    const tutor = localStorage.getItem('tutor-token');
    let [isTutor, setisTutor] = useState(null)
    const [whiteboardPresenterId, setWhiteboardPresenterId] = useState(null);

    const [activeDrawerId, setActiveDrawerId] = useState(null);
    const [tutorId, setTutorId] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);


    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        if (tutor) {
            const decoded = jwtDecode(tutor);

            if (decoded?.user?.user_type == 'tutor') {
                setisTutor(true);

            }
            else {
                setisTutor(false)

            }

        }
        else {

            setisTutor(false)
        }

    }, [])

    useEffect(() => {
        setMeetingEndTime(endTime);
    }, [endTime]);

    const { publish } = usePubSub("WHITEBOARD_PRESENTER", {
        onMessageReceived: (data) => {

            console.log("WHITEBOARD MSG =>", data);

            let parsedMessage = data.message;

            try {
                if (typeof parsedMessage === "string") {
                    parsedMessage = JSON.parse(parsedMessage);
                }

                console.log("PARSED =>", parsedMessage);
               

                if (parsedMessage?.presenterId) {
                     console.log("prasenter id ----- =>", parsedMessage.presenterId);
                    setWhiteboardPresenterId(
                        parsedMessage.presenterId
                    );
                }else{
                    console.log("NOT found Presenter id")
                }

            } catch (err) {
                console.log("WHITEBOARD PARSE ERROR", err);
            }
        }
    });

    // const { publish } = usePubSub("WHITEBOARD_PRESENTER", {
    //     onMessageReceived: (data) => {

    //         setWhiteboardPresenterId(data.message.presenterId);
    //     }

    // });

    // const { publish: publishDrawer } = usePubSub("WHITEBOARD_DRAWER", {
    //     onMessageReceived: (data) => {
    //         setActiveDrawerId(data.message.participantId || null);
    //     }
    // });


    const { publish: publishDrawer } = usePubSub("WHITEBOARD_DRAWER", {
        onMessageReceived: (data) => {

            console.log("DRAWER MSG =>", data);

            let parsedMessage = data.message;

            try {

                if (typeof parsedMessage === "string") {
                    parsedMessage = JSON.parse(parsedMessage);
                }

                console.log("DRAWER PARSED =>", parsedMessage);

                setActiveDrawerId(
                    parsedMessage?.participantId || null
                );

            } catch (err) {

                console.log("DRAWER PARSE ERROR", err);

            }
        }
    });



    const { end, leave, toggleMic, toggleWebcam, enableScreenShare, disableScreenShare, toggleScreenShare, localParticipant, participants, setQuality } = useMeeting({
        onMeetingLeft: () => {
            onMeetingLeave();
        },

    });


    useEffect(() => {
        if (localParticipant) {
            try {
                localParticipant.setQuality("high");
            } catch (e) {
                console.log("Local quality set failed", e);
            }
        }

        participants.forEach((participant) => {
            try {
                participant.setQuality("high");
            } catch (e) {
                console.log("Participant quality set failed", e);
            }
        });
    }, [participants, localParticipant]);


    const { webcamOn } = useParticipant(localParticipant?.id || "");

    const participantsArray = useMemo(() => {
        return Array.from(participants.keys());
    }, [participants]);
    const handleToggleScreenShare = async () => {
        try {
            if (!localParticipant?.streams?.size) {
                await toggleWebcam();
                await toggleMic();
                await wait(500);
            }
            if (whiteboardUrl) {
                await stopWhiteboard();
                await wait(500);
            }
            await toggleScreenShare();
        } catch (err) {
            console.error("Screen share error:", err);
        }
    };

    const ensureTracksReady = async () => {
        const localStreams = localParticipant?.streams;
        if (!localStreams || localStreams.size === 0) {
            // turn on webcam first
            await toggleWebcam();
            await wait(500);

            // turn on mic
            await toggleMic();
            await wait(500);
        }
    };


    const [showModal, setShowModal] = useState(false);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [actionType, setActionType] = useState("");


    const handleStartWhiteboard = async () => {

        await startWhiteboard();

        await wait(800);

        const presenterId = localParticipant?.id;

        console.log("WEB PRESENTER ID =>", presenterId);

        if (!presenterId) {
            console.log("Presenter ID missing");
            return;
        }

        publish(
            JSON.stringify({
                presenterId: presenterId,
            })
        );

        setWhiteboardPresenterId(presenterId);
    };

    // const handleStartWhiteboard = () => {
    //     startWhiteboard();

    //     publish({
    //         presenterId: localParticipant.id,
    //     });


    //     setWhiteboardPresenterId(localParticipant.id);

    //     // console.log(" Whiteboard started by:", localParticipant.id);
    // };

    const { startWhiteboard, stopWhiteboard, whiteboardUrl } = useWhiteboard();

    console.log("whiteboard url--", whiteboardUrl);

    console.log("dfds", localParticipant.id, "--", participantDetails, presenterId)


    const whiteboardFinalUrl = useMemo(() => {

    if (!whiteboardUrl || !localParticipant?.id) {
        return null;
    }

    if (!whiteboardPresenterId) {
        console.log("Waiting for presenter id...");
        return null;
    }

    const presenter = whiteboardPresenterId;

    const isPresenter =
        localParticipant.id === presenter;

    let finalUrl =
        `${whiteboardUrl}` +
        `&participantId=${localParticipant.id}` +
        `&hideFollowingBtn=true`;

    if (isPresenter) {
        // finalUrl += `&scrollDisabled=false`;
         finalUrl += 
        `&scrollDisabled=false` +
        `&cameraLock=false`;
    } else {
        finalUrl +=
            `&participantIdToFollow=${presenter}` +
            `&scrollDisabled=true` +
             `&cameraLock=true`;
    }

    console.log("FINAL WHITEBOARD URL =>", finalUrl);

    return finalUrl;

}, [
    whiteboardUrl,
    whiteboardPresenterId,
    localParticipant
]);


    // const whiteboardFinalUrl = useMemo(() => {

    //     if (!whiteboardUrl || !localParticipant?.id) {
    //         return null;
    //     }

    //     const presenter =
    //         whiteboardPresenterId || localParticipant.id;

    //     const isPresenter =
    //         localParticipant.id === presenter;

    //     let finalUrl =
    //         `${whiteboardUrl}` +
    //         `&participantId=${localParticipant.id}` +
    //         `&hideFollowingBtn=true`;

    //     if (isPresenter) {

    //         finalUrl += `&scrollDisabled=false`;

    //     } else {

    //         finalUrl +=
    //             `&participantIdToFollow=${presenter}` +
    //             `&scrollDisabled=true`;
    //     }

    //     console.log("FINAL WHITEBOARD URL =>", finalUrl);

    //     return finalUrl;

    // }, [
    //     whiteboardUrl,
    //     whiteboardPresenterId,
    //     localParticipant
    // ]);


   
    const [micOn, setMicOn] = useState(participantDetails?.micOn);

    // const [camOn, setCamOn] = useState(participantDetails?.camOn);
    const [addShow, setAddShow] = useState(false);
    const handleAddClose = () => setAddShow(false);
    const handleAddShow = () => {
        setAddShow(true);
        setUnreadCount(0);
    };
    const navigate = useNavigate();
    const handleLeaveMeeting = () => {
        leave();
        navigate(-1)

    }

    const handleMicToggle = () => {
        setMicOn(!micOn)
        toggleMic()
    }

    useEffect(() => {
        setMicOn(participantDetails?.micOn);
    }, [participantDetails?.micOn]);

    // useEffect(() => {
    //     setCamOn(participantDetails?.camOn);
    // }, [participantDetails?.camOn]);

    const [extraMinutes, setExtraMinutes] = useState(0);
    useEffect(() => {
        participants.forEach((participant) => {
            const data = participant?.metaData;


            if (data?.user_type === "tutor") {
                setTutorId(participant.id);
            }
        });
    }, [participants]);

    // const handleCamToggle = () => {
    //     // setCamOn(!camOn)
    //     toggleWebcam();

    //     //  Safari re-sync fix
    //     setTimeout(() => {
    //         if (localParticipant) {
    //             localParticipant.setQuality("high");
    //         }
    //     }, 800);
    // }

    const handleCamToggle = async () => {
        await toggleWebcam({
            encoderConfig: "h1080p_w1920p"
        });

        setTimeout(() => {
            if (localParticipant) {
                localParticipant.setQuality("high");
            }
        }, 1000);
    };

    const handleEndMeeting = () => {
        console.log('user', user_type)
        end();
        if (user_type == 'tutor') {
            navigate('/tutor/dashboard')
        }
        if (user_type == 'student') {
            navigate('/student/dashboard')
        }
        else {
            navigate(-1)
        }
    }

    const handleAction = async (type) => {
        if (type === "screenShare") {
            await ensureTracksReady();

            if (whiteboardUrl) {
                await stopWhiteboard();
                await wait(300);
            }

            // toggleScreenShare();
            toggleScreenShare({
                encoderConfig: "h1080p_w1920p"
            });
        }
        else if (type === "whiteboard") {
            handleStartWhiteboard();
        }
    };

    const formatDuration = (minutes) => {
        const mins = Number(minutes) || 0;

        const hours = Math.floor(mins / 60);
        const remainingMinutes = mins % 60;

        if (hours > 0 && remainingMinutes > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
        }

        if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''}`;
        }

        return `${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
    };

    const { publish: publishExtend } = usePubSub("MEETING_EXTEND", {
        onMessageReceived: (data) => {
            console.log("Received meeting extend message:", data);
            if (user_type === "student") {
                // toast.success(`Meeting extended by tutor ${data.message.extraMinutes} minutes`);
                const formattedTime = formatDuration(data.message?.extraMinutes);
                toast.success(`Meeting extended by tutor ${formattedTime}`);
            }
        }
    });


    const handleConfirm = () => {
        setShowModal(false);
        actionType === "screenShare" ? toggleScreenShare() : startWhiteboard();
    };

    const handleStopWhiteBoard = () => {
        // if (whiteboardPresenterId !== localParticipant.id) return;
        if (user_type === 'student' && whiteboardPresenterId === tutorId) return;

        stopWhiteboard();
        setShowModal(false);

        if (screenShareOn) {
            setActionType("screenShare");
        } else {
            setActionType("");
        }
    };




    const RecordingBTN = () => {
        const { startRecording, stopRecording, recordingState } = useMeeting({
            onRecordingStarted: () => {

            },
            onRecordingStopped: () => {

            }
        });
        const defaultOptions = {
            loop: true,
            autoplay: true,
            animationData: recordingBlink,
            rendererSettings: {
                preserveAspectRatio: "xMidYMid slice",
            },
            height: 64,
            width: 160,
        };

        const isRecording = useIsRecording();

        const isRecordingRef = useRef(isRecording);

        useEffect(() => {
            isRecordingRef.current = isRecording;
        }, [isRecording]);

        const { isRequestProcessing } = useMemo(
            () => ({
                isRequestProcessing:
                    recordingState === Constants.recordingEvents.RECORDING_STARTING ||
                    recordingState === Constants.recordingEvents.RECORDING_STOPPING,
            }),
            [recordingState]
        );

        const _handleClick = async () => {
            const isRecording = isRecordingRef.current;

            const config = {
                layout: {
                    type: "GRID",
                    priority: "SPEAKER",
                    gridSize: 4,
                },
                theme: "DARK",
                mode: "video-and-audio",
                quality: "high",
                orientation: "landscape",
            };

            let transcription = {
                enabled: true,
                summary: {
                    enabled: true,
                    prompt:
                        "Write summary in sections like Title, Agenda, Speakers, Action Items, Outlines, Notes and Summary",
                },
            };

            if (isRecording) {

                stopRecording();
            } else {

                try {
                    startRecording();

                } catch (error) {
                    console.error("Error starting recording:", error);
                }
            }
        };

        return (
            <>
                {/* <button className={`btn btn-secondary mx-1`} title={isRecording?'Stop Recording':'Start Recording'} onClick={_handleClick}>{isRecording?<i class='fa fa-stop' style={{fontSize:'20px'}}></i>:<i class='fas fa-record-vinyl' style={{fontSize:'20px'}}></i>}</button> */}

                <OutlinedButton

                    Icon={RecordingIcon}
                    onClick={_handleClick}
                    isFocused={isRecording}

                    lottieOption={isRecording ? defaultOptions : null}
                    isRequestProcessing={isRequestProcessing} />
            </>
        );
    };

    // pip
    const pipWindowRef = useRef();
    const getRowCount = (length) => {
        return length > 2 ? 2 : length > 0 ? 1 : 0;
    };
    const getColCount = (length) => {
        return length < 2 ? 1 : length < 5 ? 2 : 3;
    };

    const togglePipMode = async () => {
        if (pipWindowRef.current) {
            await document.exitPictureInPicture();
            pipWindowRef.current = null;
            return;
        }
        if ("pictureInPictureEnabled" in document) {

            //Stream playing here
            //Create a Canvas which will render the PiP Stream
            const source = document.createElement("canvas");
            const ctx = source.getContext("2d");

            //Create a Video tag which will popout for PiP
            const pipVideo = document.createElement("video");
            pipWindowRef.current = pipVideo;
            pipVideo.autoplay = true;

            //Create a stream from canvas which will play
            const stream = source.captureStream();
            pipVideo.srcObject = stream;

            //Do initial Canvas Paint
            drawCanvas()

            //When Video is ready, start PiP mode
            pipVideo.onloadedmetadata = () => {
                pipVideo.requestPictureInPicture();
            };
            await pipVideo.play();

            //When the PiP mode starts, draw the canvas with PiP view
            pipVideo.addEventListener("enterpictureinpicture", (event) => {
                drawCanvas();
            });

            //When PiP mode exits, dispose the tracks that were created earlier
            pipVideo.addEventListener("leavepictureinpicture", (event) => {
                pipWindowRef.current = null;
                pipVideo.srcObject.getTracks().forEach((track) => track.stop());
            });

            //This will draw all the video elements in to the Canvas
            function drawCanvas() {
                //Getting all the video elements in the document
                const videos = document.querySelectorAll("video");
                try {
                    //Perform initial black paint on the canvas
                    ctx.fillStyle = "black";
                    ctx.fillRect(0, 0, source.width, source.height);

                    //Drawing the participant videos on the canvas in the grid format
                    const rows = getRowCount(videos.length);
                    const columns = getColCount(videos.length);
                    for (let i = 0; i < rows; i++) {
                        for (let j = 0; j < columns; j++) {
                            if (j + i * columns <= videos.length || videos.length == 1) {
                                ctx.drawImage(
                                    videos[j + i * columns],
                                    j < 1 ? 0 : source.width / (columns / j),
                                    i < 1 ? 0 : source.height / (rows / i),
                                    source.width / columns,
                                    source.height / rows
                                );
                            }
                        }
                    }
                } catch (error) { }

                //If pip mode is on, keep drawing the canvas when ever new frame is requested
                if (document.pictureInPictureElement === pipVideo) {
                    requestAnimationFrame(drawCanvas);
                }
            }

        } else {
            alert("PiP is not supported by your browser");
        }
    };



    useEffect(() => {
        const interval = setInterval(() => {
            const currentTime = new Date();
            const meetingEnd = new Date(meetingEndTime);

            const timeDifference = meetingEnd.getTime() - currentTime.getTime();

            // Show a warning toast 5 minutes before ending
            if (timeDifference <= 5 * 60 * 1000 && timeDifference > 4 * 60 * 1000 && !window.toastShown) {
                toast.success('Meeting will end in 5 minutes.');
                window.toastShown = true;
            }

            console.log('currentTime', currentTime, "meetingEndTime", meetingEnd, "meetingEndTime2", meetingEndTime);

            // End meeting if time has expired
            if (timeDifference <= 0) {
                clearInterval(interval);
                end();
                toast.error('Meeting Ended');

                if (user_type === 'tutor') {
                    navigate('/tutor/dashboard');
                } else if (user_type === 'student') {
                    navigate('/student/dashboard');
                } else {
                    navigate(-1);
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [meetingEndTime, end]);

    const handleToggleWriting = () => {
        if (activeDrawerId === localParticipant.id) {
            // STOP writing
            // publishDrawer({
            //     participantId: null
            // });
            publishDrawer(
                JSON.stringify({
                    participantId: null
                })
            );
            setActiveDrawerId(null);
        } else {
            // START writing
            // publishDrawer({
            //     participantId: localParticipant.id
            // });
            publishDrawer(
                JSON.stringify({
                    participantId: localParticipant.id
                })
            );
            setActiveDrawerId(localParticipant.id);
        }
    };

    const handleAutoStartWriting = () => {
        // Already writing → do nothing
        if (activeDrawerId === localParticipant.id) return;

        // publishDrawer({
        //     participantId: localParticipant.id
        // });
        publishDrawer(
            JSON.stringify({
                participantId: localParticipant.id
            })
        );
    };


    return (
        <>
            <div className="video-controls" style={{ zIndex: 9999, position: "fixed", bottom: 0, width: "100%" }}>
                {(timeDifference <= 5 * 60 * 1000) &&
                    <MeetingTimer endTime={meetingEndTime} />
                }
                <div className="controls-container">
                    <RecordingBTN />


                    {whiteboardUrl && (
                        <button
                            className={`btn ${activeDrawerId === localParticipant.id ? "btn-danger" : "btn-success"}`}
                            onClick={handleToggleWriting}
                        >
                            {activeDrawerId === localParticipant.id ? "Stop Writing" : "Start Writing"}
                        </button>
                    )}

                    {(tutor && isTutor) ? (
                        <Dropdown>
                            <Dropdown.Toggle className="control-btn">
                                <i className="text-danger fas fa-phone-slash"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleLeaveMeeting}>Leave Call</Dropdown.Item>
                                <Dropdown.Item onClick={handleEndMeeting}>End Meeting For All</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        <button className="control-btn" onClick={handleLeaveMeeting}>
                            <i className="text-danger fas fa-phone-slash"></i>
                        </button>
                    )}

                    <button className="control-btn" onClick={handleMicToggle}>
                        {micOn
                            ? <i className="fa fa-microphone" title="Mute"></i>
                            : <i className="fa fa-microphone-slash" title="Unmute"></i>}
                    </button>

                    <button className="control-btn" onClick={handleCamToggle}>
                        {/* {participantDetails?.webcamOn */}
                        {webcamOn
                            ? <i className="fas fa-video" title="Off Camera"></i>
                            : <i className="fas fa-video-slash" title="On Camera"></i>}
                    </button>

                    <button
                        className={`control-btn ${(screenShareOn && (presenterId === participantDetails?.id)) ? 'active' : 'control-btn-invert'}`}
                        onClick={() => handleAction("screenShare")}
                    >
                        {(screenShareOn && (presenterId === participantDetails?.id)) ?
                            <img src="/assets/img/user/share.png" alt="Share" />
                            :
                            <img src="/assets/img/user/share.png" alt="Share" />
                        }
                    </button>
                    {/* <button className="control-btn" onClick={handleAddShow} title="Chat">
                        <i className="far fa-comment-dots"></i>
                    </button> */}
                    <button
                        className="control-btn"
                        onClick={handleAddShow}
                        title="Chat"
                        style={{ position: "relative" }}
                    >
                        <i className="far fa-comment-dots"></i>

                        {unreadCount > 0 && (
                            <span style={{
                                position: "absolute",
                                top: "-6px",
                                right: "-6px",
                                minWidth: "20px",
                                height: "20px",
                                background: "#ff3b30",
                                color: "#fff",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "600",
                                padding: "0 6px",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                border: "2px solid #000"
                            }}>
                                {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                        )}
                    </button>

                    {!whiteboardUrl ? (
                        <button
                            className='btn btn-secondary'
                            style={{ border: '2px solid #848884' }}
                            onClick={() => handleAction("whiteboard")}

                        >
                            Start Whiteboard
                        </button>
                    ) : (
                        <button
                            className='btn btn-secondary'
                            style={{ border: '2px solid #848884', zIndex: 2 }}
                            onClick={handleStopWhiteBoard}
                            disabled={user_type === 'student' && whiteboardPresenterId === tutorId}
                            title={
                                user_type === 'student' && whiteboardPresenterId !== localParticipant.id
                                    ? "You cannot stop the tutor's whiteboard"
                                    : "Stop Whiteboard"
                            }
                        >
                            Stop Whiteboard
                        </button>
                    )}


                    {user_type === 'tutor' &&

                        <button className="control-btn" onClick={() => setShowExtendModal(true)} title="Extend meeting">
                            <i className="fa fa-info-circle"></i>
                        </button>}

                </div>

            </div>

            {/* {whiteboardFinalUrl && (
                <div
                    style={{
                        height: "90%",
                        width: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <iframe
                        className="white-board-area"
                        src={whiteboardFinalUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            pointerEvents:
                                localParticipant.id === whiteboardPresenterId
                                    ? "auto"
                                    : "none",
                        }}
                    />
                </div>
            )} */}

            {whiteboardFinalUrl && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    display: "flex",
                    background: "#000"
                }}>

                    <div style={{
                        width: "250px",
                        height: "100%",
                        overflowY: "auto",
                        background: "#111",
                        padding: "10px",
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "10px"
                    }}>
                        {participantsArray.map((id) => {
                            const isActive = id === activeDrawerId;

                            return (
                                <div key={`participant-${id}`} style={{
                                    width: "100%",
                                    height: "160px",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    background: "#000",
                                    border: isActive ? "3px solid #00ff00" : "1px solid #333",
                                    position: "relative"
                                }}>


                                    {isActive && (
                                        <div style={{
                                            position: "absolute",
                                            top: "5px",
                                            left: "5px",
                                            background: "green",
                                            color: "#fff",
                                            padding: "2px 6px",
                                            fontSize: "10px",
                                            borderRadius: "5px",
                                            zIndex: 2
                                        }}>
                                            🟢 Writing...
                                        </div>
                                    )}

                                    <ParticipantView
                                        key={id}
                                        participantId={id}
                                        small
                                        isHost={id === tutorId}
                                        activeDrawerId={activeDrawerId}
                                        localParticipant={localParticipant}
                                        tutorId={tutorId}
                                    />

                                    {/* <ParticipantView participantId={id} small /> */}
                                    {/* <ParticipantView
                                        participantId={id}
                                        small
                                        isHost={id === tutorId}
                                    /> */}

                                </div>
                            );
                        })}
                    </div>

                    <div style={{
                        position: "relative",
                        width: "100%",
                        height: "84%"
                    }}>

                        <div
                            onClick={handleAutoStartWriting}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                zIndex: 2,
                                cursor: activeDrawerId === localParticipant.id ? "default" : "crosshair",
                                background: "transparent",
                                pointerEvents: activeDrawerId === localParticipant.id ? "none" : "auto"
                            }}
                        />

                        {/* <iframe
                            src={whiteboardFinalUrl} */}
                        <iframe
                            src={whiteboardFinalUrl}
                            allow="camera; microphone; fullscreen; display-capture"
                            allowFullScreen
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                position: "relative",
                                zIndex: 1
                            }}
                        />
                    </div>


                    {/* <div style={{
                        flex: 1,
                        height: "100%",
                        background: "#fff"
                    }}>
                        <iframe
                            src={whiteboardFinalUrl}
                            style={{
                                width: "100%",
                                height: "84%",
                                border: "none"
                            }}
                        // onLoad={() => {
                        //     const iframe = document.querySelector("iframe");

                        //     iframe?.contentWindow?.addEventListener("pointerdown", () => {
                        //         publishDrawer({
                        //             participantId: localParticipant.id
                        //         });
                        //     });
                        // }}
                        />
                    </div> */}

                </div>
            )}

            {/* <Chat show={addShow} handleClose={handleAddClose} user_name={user_name} /> */}

            <Chat
                show={addShow}
                handleClose={handleAddClose}
                user_name={user_name}
                setUnreadCount={setUnreadCount}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                show={showModal}
                onConfirm={handleConfirm}
                onCancel={() => setShowModal(false)}
                message="A screen or whiteboard is already being shared. Do you still want to continue?"
            />
            <MeetingExtend
                data={location}
                onExtendSuccess={(newEndTime, extraMinutes) => {
                    setMeetingEndTime(newEndTime);
                    console.log("Meeting extended. New end time:", newEndTime, "Extra minutes:", extraMinutes);
                    setExtraMinutes(extraMinutes);
                    publishExtend({
                        message: "extended",
                        extraMinutes: extraMinutes
                    });
                }}

                show={showExtendModal}
                onCancel={() => setShowExtendModal(false)}
            />

        </>

    );
}

export default Controls;